import AppointmentSaveEffects from './AppointmentSaveEffects';
import React, { useState, useEffect, useRef } from 'react';
import config from '../config/environment';
import apiService, { authenticatedFetch } from '../services/api';
import { formatFileSize, formatUploadDate } from '../utils/dateUtils';
import { normalizeText } from '../utils/normalizeText';
import { Trash2, Upload, Eye, X, Check, Send, ExternalLink, ArrowLeft, User, Calendar, Search, Phone, Bell, Mail, Repeat, DoorOpen } from 'lucide-react';
import { Event } from '../types/Event';
import SearchableSelect from './SearchableSelect';
import ModalShell, { ModalHeader, ModalBody, ModalFooter } from './ModalShell';
import SendMessagePanel from './SendMessagePanel';
import { useTranslation } from 'react-i18next';

import { useDialog } from './AppDialog';
import { useAuth } from '../contexts/AuthContext';
import { isWithinBusinessHours } from '../utils/businessHours';
import logger from '../utils/logger';
interface Client {
  id: number;
  name: string;
  email?: string;
  phone?: string;
}

interface Procedure {
  id: number;
  name: string;
  durationMinutes: number;
  price?: number;
  color?: string;
}

function hexToAlpha(hex: string, alpha: number): string {
  const h = (hex || '#3788d8').replace('#', '').padEnd(6, '0');
  const r = parseInt(h.slice(0, 2), 16) || 55;
  const g = parseInt(h.slice(2, 4), 16) || 136;
  const b = parseInt(h.slice(4, 6), 16) || 216;
  return `rgba(${r},${g},${b},${alpha})`;
}

interface Resource {
  id: number;
  name: string;
  color: string;
  description?: string;
}

interface Picture {
  id: number;
  filename: string;
  original_name: string;
  file_size: number;
  mime_type: string;
  description: string | null;
  uploaded_at: string;
}

interface EventModalProps {
  isOpen: boolean;
  event: Event | null;
  clients: Client[];
  procedures: Procedure[];
  onClose: () => void;
  onSave: (event: Omit<Event, 'id'>) => Promise<void> | void;
  onDelete?: () => void;
  onEditClient?: (clientId: number) => void;
  onOpenPatientFeed?: (clientId: number) => void;
  onClientCreate?: (name: string) => Promise<Client | null>;
  onClientUpdate?: (clientId: number, data: { name: string; email?: string; phone?: string; address?: string; dateOfBirth?: string; notes?: string }) => Promise<Client | null>;
  onClientFetch?: (clientId: number) => Promise<{ name: string; email?: string; phone?: string; address?: string; dateOfBirth?: string; notes?: string; photo_path?: string | null } | null>;
  defaultWhatsappReminder?: boolean;
  defaultEmailReminder?: boolean;
  providers?: { id: number; name: string; calendar_color: string | null; default_resource_id?: number | null }[];
}

const EventModal: React.FC<EventModalProps> = ({ isOpen, event, clients, procedures, onClose, onSave, onDelete, onEditClient, onOpenPatientFeed, onClientCreate, onClientUpdate, onClientFetch, defaultWhatsappReminder = true, defaultEmailReminder = true, providers = [] }) => {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useDialog();
  const { tenantSettings, user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [inlineEditClient, setInlineEditClient] = useState<{ id: number } | null>(null);
  const [clientFormData, setClientFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    dateOfBirth: '',
    notes: '',
  });
  const [clientSaving, setClientSaving] = useState(false);
  const [clientPhotoUrl, setClientPhotoUrl] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    start: new Date(),
    end: new Date(),
    color: '#1a73e8',
    description: '',
    location: '',
    allDay: false,
    clientId: undefined as number | undefined,
    procedureId: undefined as number | undefined,
    notes: '',
    durationMinutes: 30,
    providerId: undefined as number | undefined,
    recurrenceRule: '' as string,
  });
  const [recurrenceEditMode, setRecurrenceEditMode] = useState<'this' | 'following' | 'all' | null>(null);
  const [saving, setSaving] = useState(false);

  // Multi-select procedures
  const [selectedProcedureIds, setSelectedProcedureIds] = useState<number[]>([]);
  const [procedureSearch, setProcedureSearch] = useState('');

  // Resources (salas)
  const [availableResources, setAvailableResources] = useState<Resource[]>([]);
  const [selectedResourceIds, setSelectedResourceIds] = useState<number[]>([]);
  // True once the user manually toggles a room (or when editing an existing event),
  // so the provider's default-room auto-selection never clobbers a deliberate pick.
  const resourcesTouchedRef = useRef(false);

  // Pre-consultation forms: send termo (consent) / anamnese (intake) links along
  // with the appointment reminder. Toggles only show when the clinic has at
  // least one active template of that type.
  const [hasConsentTemplates, setHasConsentTemplates] = useState(false);
  const [hasIntakeTemplates, setHasIntakeTemplates] = useState(false);
  const [sendConsent, setSendConsent] = useState(false);
  const [sendIntake, setSendIntake] = useState(false);

  // Photo management state
  const [pictures, setPictures] = useState<Picture[]>([]);
  const [showPhotosSection, setShowPhotosSection] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [pictureDescription, setPictureDescription] = useState('');
  const [selectedPicture, setSelectedPicture] = useState<Picture | null>(null);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [imageZoom, setImageZoom] = useState(1);

  // Message sending state
  const [showSendMessage, setShowSendMessage] = useState(false);

  // ── Teleconsulta state (Onda 3B) ─────────────────────────────────────────
  const [teleconsultaOpen, setTeleconsultaOpen] = useState(false);
  const [videoProvider, setVideoProvider] = useState<string>('none');
  const [meetUrl, setMeetUrl] = useState<string>('');

  // Helper function to format time for input
  const formatTimeForInput = (date: Date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Helper function to format date for <input type="date"> using LOCAL
  // date components (toISOString() converts to UTC, which shifts the day
  // for timezones behind UTC — e.g. an 11:30 BRT appointment near midnight).
  const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to parse time input and round to nearest valid minute
  const parseTimeInput = (timeString: string, baseDate: Date) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const validMinutes = [0, 15, 30, 45];
    const closestMinute = validMinutes.reduce((prev, curr) => 
      Math.abs(curr - minutes) < Math.abs(prev - minutes) ? curr : prev
    );
    
    const newDate = new Date(baseDate);
    newDate.setHours(hours, closestMinute, 0, 0);
    return newDate;
  };

  useEffect(() => {
    if (event) {
      const computedDuration = event.end && event.start
        ? Math.round((new Date(event.end).getTime() - new Date(event.start).getTime()) / 60000)
        : 30;
      const snapDurations = [15, 30, 45, 60, 75, 90, 120, 180];
      const snappedDuration = snapDurations.reduce((prev, curr) =>
        Math.abs(curr - computedDuration) < Math.abs(prev - computedDuration) ? curr : prev
      );
      setFormData({
        title: event.title,
        start: event.start,
        end: event.end,
        color: event.color,
        description: event.description || '',
        location: event.location || '',
        allDay: event.allDay || false,
        clientId: event.clientId,
        procedureId: event.procedureId,
        notes: event.notes || '',
        durationMinutes: snappedDuration,
        providerId: event.providerId,
        recurrenceRule: event.recurrenceRule || '',
      });

      const initialProcIds = event.procedureIds && event.procedureIds.length
        ? event.procedureIds
        : (event.procedureId ? [event.procedureId] : []);
      setSelectedProcedureIds(initialProcIds);
      setProcedureSearch('');
      setReminders({
        wa: event.whatsappReminderEnabled !== undefined ? event.whatsappReminderEnabled : defaultWhatsappReminder,
        email: event.emailReminderEnabled !== undefined ? event.emailReminderEnabled : defaultEmailReminder,
      });
      setSendConsent(!!event.sendConsentWithReminder);
      setSendIntake(!!event.sendIntakeWithReminder);

      // Teleconsulta fields (Onda 3B)
      const evAny = event as any;
      const existingProvider = evAny.video_provider || evAny.videoProvider || 'none';
      const existingMeetUrl = evAny.meet_url || evAny.meetUrl || '';
      setVideoProvider(existingProvider);
      setMeetUrl(existingMeetUrl);
      if (existingProvider && existingProvider !== 'none') setTeleconsultaOpen(true);

      // Load pictures if editing existing event
      if (event.id) {
        fetchPictures(parseInt(event.id));
        setShowPhotosSection(true);
        // Editing an existing event: its saved rooms are the source of truth, so
        // never let default-room auto-selection touch them.
        resourcesTouchedRef.current = true;
        // Load existing resource assignments
        authenticatedFetch(`${config.apiUrl}/api/appointments/${event.id}/resources`)
          .then(r => r.json())
          .then(d => { if (d.success) setSelectedResourceIds((d.data || []).map((r: Resource) => r.id)); })
          .catch(() => {});
      } else {
        resourcesTouchedRef.current = false;
        setSelectedResourceIds([]);
      }
    } else {
      const now = new Date();
      // Round to nearest 15-minute slot so the time select has a matching option
      const roundedNow = new Date(now);
      roundedNow.setMinutes(Math.round(now.getMinutes() / 15) * 15, 0, 0);
      setFormData({
        title: '',
        start: roundedNow,
        end: new Date(roundedNow.getTime() + 30 * 60 * 1000),
        color: '#1a73e8',
        description: '',
        location: '',
        allDay: false,
        clientId: undefined,
        procedureId: undefined,
        notes: '',
        durationMinutes: 30,
        providerId: undefined,
        recurrenceRule: '',
      });
      setSelectedProcedureIds([]);
      resourcesTouchedRef.current = false;
      setSelectedResourceIds([]);
      setProcedureSearch('');
      setReminders({ wa: defaultWhatsappReminder, email: defaultEmailReminder });
      setSendConsent(false);
      setSendIntake(false);
      setPictures([]);
      setShowPhotosSection(false);
      setSelectedFiles([]);
      // Reset teleconsulta
      setVideoProvider('none');
      setMeetUrl('');
      setTeleconsultaOpen(false);
    }
  }, [event, defaultWhatsappReminder, defaultEmailReminder]);

  // When the modal closes, discard any in-progress inline patient edit. The
  // EventModal component stays mounted (ModalShell only unmounts its children),
  // so without this reset `inlineEditClient` persists and the NEXT event click
  // reopens the modal straight into the "Editar paciente" screen instead of the
  // event form.
  useEffect(() => {
    if (isOpen) return;
    setInlineEditClient(null);
    setClientPhotoUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, [isOpen]);

  // Fetch available resources for this tenant
  useEffect(() => {
    if (!isOpen) return;
    authenticatedFetch(`${config.apiUrl}/api/admin/resources`)
      .then(r => r.json())
      .then(d => { if (d.success) setAvailableResources((d.data || []).filter((r: Resource) => (r as any).active !== false)); })
      .catch(() => {});
  }, [isOpen]);

  // Pre-select the responsible provider's default room on a NEW appointment. The
  // provider is the one picked in the selector (admin) or, when none is chosen,
  // the logged-in user. Runs only until the user touches the room selection
  // (resourcesTouchedRef) so it never overrides a manual pick or an edited event.
  useEffect(() => {
    if (!isOpen || event?.id || resourcesTouchedRef.current) return;
    if (availableResources.length === 0 || providers.length === 0) return;
    const targetProviderId = formData.providerId ?? user?.id;
    if (!targetProviderId) return;
    const provider = providers.find(p => p.id === targetProviderId);
    const defaultId = provider?.default_resource_id ?? null;
    // Only apply when the default room still exists and is active for this tenant.
    if (defaultId && availableResources.some(r => r.id === defaultId)) {
      setSelectedResourceIds([defaultId]);
    }
  }, [isOpen, event, availableResources, providers, formData.providerId, user]);

  // Whether a consent (termo) / intake (anamnese) template actually RESOLVES for
  // the currently-selected procedure(s). Re-checked when the procedures change so
  // the toggles only appear when a link can really be generated for this
  // appointment — never showing an option that would silently send nothing.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    authenticatedFetch(`${config.apiUrl}/api/appointments/form-availability`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ procedure_ids: selectedProcedureIds }),
    })
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        setHasConsentTemplates(!!(d.success && d.data && d.data.consent));
        setHasIntakeTemplates(!!(d.success && d.data && d.data.intake));
      })
      .catch(() => { if (!cancelled) { setHasConsentTemplates(false); setHasIntakeTemplates(false); } });
    return () => { cancelled = true; };
  }, [isOpen, selectedProcedureIds]);

  const fetchPictures = async (appointmentId: number) => {
    logger.log('EventModal - fetchPictures called with appointmentId:', appointmentId);
    try {
      const response = await authenticatedFetch(`${config.apiUrl}/api/appointment-notes/${appointmentId}/pictures`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      logger.log('EventModal - pictures response status:', response.status);
      
      if (!response.ok) {
        throw new Error('Failed to fetch pictures');
      }

      const data = await response.json();
      logger.log('EventModal - pictures response data:', data);
      
      if (data.success) {
        setPictures(data.data || []);
        logger.log('EventModal - set pictures:', data.data?.length || 0);
      } else {
        logger.error('Failed to fetch pictures:', data.message);
        setPictures([]);
      }
    } catch (err) {
      logger.error('Error fetching pictures:', err);
      setPictures([]);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const validFiles: File[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file.type.startsWith('image/')) {
          await showAlert(`File ${file.name} is not an image file. Only images are allowed.`);
          continue;
        }
        if (file.size > 10 * 1024 * 1024) {
          await showAlert(`File ${file.name} is too large. File size must be less than 10MB.`);
          continue;
        }
        validFiles.push(file);
      }
      setSelectedFiles(validFiles);
    }
  };

  const handleUploadPicture = async () => {
    if (!event?.id || selectedFiles.length === 0) return;

    try {
      setUploadingPicture(true);
      const uploadPromises = selectedFiles.map(async (file) => {
        const formData = new FormData();
        formData.append('picture', file);
        formData.append('description', pictureDescription);

        const response = await authenticatedFetch(`${config.apiUrl}/api/appointment-notes/${event.id}/pictures`, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Failed to upload ${file.name}`);
        }

        return response.json();
      });

      const results = await Promise.all(uploadPromises);
      
      // Add all uploaded pictures to the list
      const newPictures = results.map(data => data.picture);
      setPictures(prev => [...newPictures, ...prev]);
      
      setSelectedFiles([]);
      setPictureDescription('');
      
      // Reset file input
      const fileInput = document.getElementById('picture-upload-event') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
      await showAlert(`Successfully uploaded ${selectedFiles.length} photo(s)!`);
    } catch (err) {
      logger.error('Error uploading pictures:', err);
      await showAlert('Failed to upload some pictures. Please try again.');
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleDeletePicture = async (pictureId: number) => {
    if (!event?.id) return;

    if (!await showConfirm('Are you sure you want to delete this picture?', { danger: true })) return;

    try {
      const response = await authenticatedFetch(`${config.apiUrl}/api/appointment-notes/${event.id}/pictures/${pictureId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete picture');
      }

      setPictures(prev => prev.filter(pic => pic.id !== pictureId));
    } catch (err) {
      logger.error('Error deleting picture:', err);
      await showAlert('Failed to delete picture');
    }
  };


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSave();
  };

  const handleSave = async () => {
    let finalFormData = { ...formData };

    if (formData.clientId && !formData.title.trim()) {
      const selectedClient = clients.find(client => client.id === formData.clientId);
      const selectedProcedure = formData.procedureId
        ? procedures.find(procedure => procedure.id === formData.procedureId)
        : null;

      if (selectedClient) {
        const autoTitle = selectedProcedure
          ? `${selectedClient.name} - ${selectedProcedure.name}`
          : selectedClient.name;
        finalFormData.title = autoTitle;
        setFormData(prev => ({ ...prev, title: autoTitle }));
      }
    }

    if (!(finalFormData.start instanceof Date) || isNaN(finalFormData.start.getTime())) {
      await showAlert(t('calendar.invalidStartDate'));
      return;
    }

    if (!(finalFormData.end instanceof Date) || isNaN(finalFormData.end.getTime())) {
      await showAlert(t('calendar.invalidEndDate'));
      return;
    }

    if (!finalFormData.title.trim()) {
      await showAlert(t('calendar.enterTitle'));
      return;
    }

    // Soft business-hours check: warn (don't block) when the appointment falls
    // outside the clinic's configured hours. No hours configured → always allowed.
    if (!isWithinBusinessHours(finalFormData.start, tenantSettings?.businessHours)) {
      const proceed = await showConfirm(
        t('calendar.outsideBusinessHours', 'Este horário está fora do horário de funcionamento da clínica. Deseja agendar mesmo assim?'),
        { title: t('calendar.outsideBusinessHoursTitle', 'Fora do horário'), confirmLabel: t('common.continue', 'Continuar') }
      );
      if (!proceed) return;
    }

    // Resource (sala) double-booking check: warn (don't block) when a selected
    // resource is already booked in an overlapping appointment. The check-conflict
    // route is read-only and tenant-scoped; network/route failure never blocks save.
    if (selectedResourceIds.length > 0) {
      try {
        const startIso = finalFormData.start.toISOString();
        const endIso = finalFormData.end.toISOString();
        const results = await Promise.all(
          selectedResourceIds.map(async (rid) => {
            const resp = await authenticatedFetch(`${config.apiUrl}/api/admin/resources/${rid}/check-conflict`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ start_time: startIso, end_time: endIso, exclude_appointment_id: event?.id }),
            });
            const d = await resp.json().catch(() => null);
            return { rid, conflict: !!(d && d.success && d.conflict) };
          })
        );
        const conflicting = results
          .filter(r => r.conflict)
          .map(r => availableResources.find(a => a.id === r.rid)?.name)
          .filter(Boolean);
        if (conflicting.length > 0) {
          const proceed = await showConfirm(
            t('calendar.resourceConflict', 'A(s) sala(s) {{names}} já está(ão) reservada(s) neste horário. Deseja agendar mesmo assim?', { names: conflicting.join(', ') }),
            { title: t('calendar.resourceConflictTitle', 'Conflito de sala'), confirmLabel: t('common.continue', 'Continuar') }
          );
          if (!proceed) return;
        }
      } catch {
        // Conflict check failed (offline/route error) — proceed without blocking.
      }
    }

    // Recurring event: ask edit scope before saving (edit mode is chosen in UI)
    const isRecurring = !!(event?.recurrenceGroupId);
    if (isRecurring && recurrenceEditMode === null) {
      setRecurrenceEditMode('this'); // trigger the picker dialog below
      return;
    }

    setSaving(true);
    try {
      await onSave({
        ...finalFormData,
        procedureId: selectedProcedureIds[0],
        procedureIds: selectedProcedureIds,
        whatsappReminderEnabled: reminders.wa,
        emailReminderEnabled: reminders.email,
        recurrenceRule: finalFormData.recurrenceRule || undefined,
        recurrenceEditMode: recurrenceEditMode ?? undefined,
        resourceIds: selectedResourceIds,
        sendConsentWithReminder: hasConsentTemplates && sendConsent,
        sendIntakeWithReminder: hasIntakeTemplates && sendIntake,
        // Teleconsulta (Onda 3B)
        video_provider: videoProvider !== 'none' ? videoProvider : undefined,
        meet_url: meetUrl || undefined,
      } as any);
      onClose();
    } catch (err) {
      await showAlert(`Failed to save: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    if (event?.recurrenceGroupId) {
      const mode = await new Promise<'this' | 'following' | 'all' | null>(resolve => {
        showConfirm(
          t('calendar.deleteRecurring', 'Este é um evento recorrente. O que deseja excluir?'),
          {
            danger: true,
            confirmLabel: t('calendar.deleteThis', 'Só este'),
            cancelLabel: t('common.cancel'),
          }
        ).then(ok => resolve(ok ? 'this' : null));
      });
      if (mode === null) return;
      // Pass mode back via a custom property on the event so App.tsx can handle
      (event as any)._deleteRecurrenceMode = mode;
    }
    onDelete();
  };

  const handleClientSave = async () => {
    if (!inlineEditClient || !onClientUpdate) return;
    setClientSaving(true);
    try {
      const updated = await onClientUpdate(inlineEditClient.id, {
        name: clientFormData.name,
        email: clientFormData.email || undefined,
        phone: clientFormData.phone || undefined,
        address: clientFormData.address || undefined,
        dateOfBirth: clientFormData.dateOfBirth || undefined,
        notes: clientFormData.notes || undefined,
      });
      if (updated) {
        setInlineEditClient(null);
        if (clientPhotoUrl) {
          URL.revokeObjectURL(clientPhotoUrl);
          setClientPhotoUrl(null);
        }
      }
    } catch (err) {
      await showAlert(`Failed to save client: ${err instanceof Error ? err.message : 'Unknown error'}`, { title: 'Error' });
    } finally {
      setClientSaving(false);
    }
  };

  const handleBackToEvent = () => {
    setInlineEditClient(null);
    if (clientPhotoUrl) {
      URL.revokeObjectURL(clientPhotoUrl);
      setClientPhotoUrl(null);
    }
  };

  const startInlineClientEdit = async (clientId: number) => {
    if (!onClientFetch) return;
    const clientData = await onClientFetch(clientId);
    if (clientData) {
      setClientFormData({
        name: clientData.name || '',
        email: clientData.email || '',
        phone: clientData.phone || '',
        address: clientData.address || '',
        dateOfBirth: clientData.dateOfBirth || '',
        notes: clientData.notes || '',
      });
      if (clientData.photo_path) {
        try {
          const res = await authenticatedFetch(`${config.apiUrl}/api/patients/${clientId}/photo`);
          if (res.ok) {
            const blob = await res.blob();
            setClientPhotoUrl(URL.createObjectURL(blob));
          }
        } catch (_e) { /* ignore */ }
      } else {
        setClientPhotoUrl(null);
      }
      setInlineEditClient({ id: clientId });
    }
  };

  const colorOptions = [
    '#0b3b2e', '#4a6b58', '#b08544', '#8b4b3d', '#2f3a6b', '#7a4d6e', '#d4b896',
    '#1a73e8', '#10b981', '#f59e0b', '#ef4444', '#ec4899',
  ];

  const [reminders, setReminders] = useState({ wa: defaultWhatsappReminder, email: defaultEmailReminder });

  const primaryProcedureId = selectedProcedureIds[0];
  const selectedProcedure = primaryProcedureId
    ? procedures.find(p => p.id === primaryProcedureId)
    : null;

  const formatDateLong = (d: Date) => {
    if (!(d instanceof Date) || isNaN(d.getTime())) return '';
    return d.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const formatTime = (d: Date) => {
    if (!(d instanceof Date) || isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const toggleProcedure = (procedureId: number) => {
    setSelectedProcedureIds(prev => {
      const next = prev.includes(procedureId)
        ? prev.filter(id => id !== procedureId)
        : [...prev, procedureId];

      const primaryId = next[0];
      const primaryProc = procedures.find(p => p.id === primaryId);

      // Duration = sum of selected procedures' durations (fallback 30 when none)
      const totalDuration = next.reduce((sum, id) => {
        const p = procedures.find(pr => pr.id === id);
        return sum + (p?.durationMinutes || 30);
      }, 0) || 30;

      setFormData(fd => {
        const updated: typeof fd = { ...fd, procedureId: primaryId };
        if (fd.start instanceof Date && !isNaN(fd.start.getTime())) {
          const newEnd = new Date(fd.start.getTime() + totalDuration * 60 * 1000);
          if (!isNaN(newEnd.getTime())) {
            updated.end = newEnd;
            updated.durationMinutes = totalDuration;
          }
        }
        // Auto-title using the PRIMARY procedure (mirror legacy behavior)
        if (primaryProc && fd.clientId) {
          const client = clients.find(c => c.id === fd.clientId);
          if (client) {
            const currentTitle = fd.title.trim();
            if (!currentTitle || currentTitle === client.name ||
                clients.some(c => currentTitle === c.name) ||
                clients.some(c => procedures.some(p => currentTitle === `${c.name} - ${p.name}`))) {
              updated.title = `${client.name} - ${primaryProc.name}`;
            }
          }
        }
        return updated;
      });

      return next;
    });
  };

  const toggleResource = (id: number) => {
    resourcesTouchedRef.current = true;
    setSelectedResourceIds(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  };

  const handleClientSelect = (value: string | number | undefined) => {
    const clientId = value as number | undefined;
    if (clientId && !formData.title.trim()) {
      const client = clients.find(c => c.id === clientId);
      if (client) {
        const sp = formData.procedureId
          ? procedures.find(p => p.id === formData.procedureId)
          : null;
        const autoTitle = sp
          ? `${client.name} - ${sp.name}`
          : client.name;
        setFormData({ ...formData, clientId, title: autoTitle });
        return;
      }
    }
    setFormData({ ...formData, clientId });
  };

  return (
    <ModalShell isOpen={isOpen} onClose={onClose}>
      {inlineEditClient && onClientUpdate ? (
        <>
          <ModalHeader
            tag={<><User size={11} /> {t('clients.editClient', 'Editar paciente')}</>}
            title={clientFormData.name || t('clients.editClient', 'Editar paciente')}
            onClose={onClose}
          />
          <ModalBody single>
              <button
                type="button"
                onClick={handleBackToEvent}
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 20, padding: '4px 0' }}
              >
                <ArrowLeft size={14} />
                {t('calendar.backToEvent', 'Voltar ao agendamento')}
              </button>

              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--sand-soft)', border: '1px solid var(--line)', display: 'grid', placeItems: 'center', overflow: 'hidden' }}>
                  {clientPhotoUrl ? (
                    <img src={clientPhotoUrl} alt={clientFormData.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <User size={28} style={{ color: 'var(--ink-faint)' }} />
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="field">
                  <label className="field-label">{t('common.name')} *</label>
                  <input type="text" required value={clientFormData.name} onChange={(e) => setClientFormData({ ...clientFormData, name: e.target.value })} />
                </div>
                <div className="field">
                  <label className="field-label">{t('common.phone')}</label>
                  <input type="text" value={clientFormData.phone} onChange={(e) => setClientFormData({ ...clientFormData, phone: e.target.value })} placeholder="+55 11 99999-9999" style={{ fontFamily: 'var(--font-mono)' }} />
                </div>
                <div className="field">
                  <label className="field-label">{t('common.email')}</label>
                  <input type="email" value={clientFormData.email} onChange={(e) => setClientFormData({ ...clientFormData, email: e.target.value })} />
                </div>
                <div className="field">
                  <label className="field-label">{t('common.address')}</label>
                  <input type="text" value={clientFormData.address} onChange={(e) => setClientFormData({ ...clientFormData, address: e.target.value })} />
                </div>
              </div>
              <div className="field">
                <label className="field-label">{t('clients.birthDate')}</label>
                <input type="date" value={clientFormData.dateOfBirth} onChange={(e) => setClientFormData({ ...clientFormData, dateOfBirth: e.target.value })} />
              </div>
              <div className="field">
                <label className="field-label">{t('common.notes')}</label>
                <textarea value={clientFormData.notes} onChange={(e) => setClientFormData({ ...clientFormData, notes: e.target.value })} rows={3} />
              </div>
          </ModalBody>
          <ModalFooter>
            <div className="spacer" />
            <button className="btn-s ghost" type="button" onClick={handleBackToEvent}>{t('common.cancel')}</button>
            <button className="btn-s primary" type="button" onClick={handleClientSave} disabled={clientSaving || !clientFormData.name.trim()}>
              <Check size={14} />
              {clientSaving ? t('common.saving', 'Salvando…') : t('common.save')}
            </button>
          </ModalFooter>
        </>
      ) : (
        <>
          <ModalHeader
            tag={<><Calendar size={11} /> {event?.id ? t('calendar.editEvent') : (t('calendar.createEvent', 'Novo agendamento'))}</>}
            title={formData.start instanceof Date && !isNaN(formData.start.getTime()) ? formatDateLong(formData.start) : (event?.id ? t('calendar.editEvent') : t('calendar.createEvent'))}
            subtitle={
              formData.start instanceof Date && !isNaN(formData.start.getTime()) ? (
                <>
                  <span>{formatTime(formData.start)} – {formatTime(formData.end)}</span>
                  <span className="dot" />
                  <span>{formData.durationMinutes} min</span>
                  {selectedProcedure && <><span className="dot" /><span>{selectedProcedure.name}</span></>}
                </>
              ) : undefined
            }
            onClose={onClose}
          />

          <ModalBody split>
            {/* LEFT — form */}
            <div className="left">
              <form onSubmit={handleSubmit}>
                {/* Title */}
                <div className="field">
                  <label className="field-label" htmlFor="eventTitle">{t('calendar.eventTitle', 'Título')}</label>
                  <input
                    id="eventTitle"
                    type="text"
                    aria-label="Title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={t('calendar.eventTitle')}
                  />
                  {formData.clientId && !formData.title.trim() && (
                    <div className="hint" style={{ color: 'var(--accent)' }}>
                      {formData.procedureId
                        ? `Título: ${clients.find(c => c.id === formData.clientId)?.name} - ${procedures.find(p => p.id === formData.procedureId)?.name}`
                        : `Título: ${clients.find(c => c.id === formData.clientId)?.name}`
                      }
                    </div>
                  )}
                </div>

                {/* Client */}
                <div className="field">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label className="field-label" id="clientLabel">{t('calendar.client', 'Paciente')}</label>
                    {formData.clientId && (onOpenPatientFeed || onClientUpdate || onEditClient) && (
                      <button
                        type="button"
                        onClick={() => {
                          if (onOpenPatientFeed) {
                            onOpenPatientFeed(formData.clientId as number);
                          } else if (onClientUpdate && onClientFetch) {
                            startInlineClientEdit(formData.clientId as number);
                          } else if (onEditClient) {
                            onEditClient(formData.clientId as number);
                          }
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', padding: 10, minWidth: 44, minHeight: 44, touchAction: 'manipulation' }}
                        title={t('calendar.viewPatient', 'Ver paciente')}
                        aria-label={t('calendar.viewPatient', 'Ver paciente')}
                      >
                        <ExternalLink size={12} />
                      </button>
                    )}
                    {formData.clientId && (() => {
                      const selectedClient = clients.find(c => c.id === formData.clientId);
                      const phone = selectedClient?.phone;
                      if (!phone) return null;
                      const digits = phone.replace(/\D/g, '');
                      const waNumber = digits.startsWith('55') ? digits : `55${digits}`;
                      return (
                        <a
                          href={`https://wa.me/${waNumber}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ display: 'flex', alignItems: 'center', color: '#25D366', padding: 10, minWidth: 44, minHeight: 44, touchAction: 'manipulation' }}
                          title={`WhatsApp: ${phone}`}
                          aria-label="Abrir chat no WhatsApp"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                        </a>
                      );
                    })()}
                  </div>
                  <SearchableSelect
                    id="clientSelect"
                    options={clients ? clients.map(client => ({
                      value: client.id,
                      label: `${client.name}${client.email ? ` (${client.email})` : ''}`,
                      ...client
                    })) : []}
                    value={formData.clientId}
                    onChange={handleClientSelect}
                    placeholder={t('calendar.selectClient')}
                    searchPlaceholder={t('calendar.searchClients', 'Buscar paciente…')}
                    clearable={true}
                    ariaLabel="Client"
                    ariaLabelledBy="clientLabel"
                    onCreateNew={onClientCreate ? async (name) => {
                      const created = await onClientCreate(name);
                      if (created) {
                        const sp = formData.procedureId
                          ? procedures.find(p => p.id === formData.procedureId)
                          : null;
                        const autoTitle = sp
                          ? `${created.name} - ${sp.name}`
                          : created.name;
                        setFormData(prev => ({
                          ...prev,
                          clientId: created.id,
                          title: prev.title.trim() ? prev.title : autoTitle,
                        }));
                      }
                    } : undefined}
                    createNewLabel={(term) => `${t('calendar.createClient', 'Cadastrar paciente')}: "${term}"`}
                  />
                </div>

                {/* Procedure — searchable multi-select */}
                <div className="field">
                  <label className="field-label" id="procedureLabel">{t('calendar.procedure', 'Procedimento')}</label>
                  <div className="proc-multi">
                    <div className="proc-multi-control">
                      {selectedProcedureIds.map(id => {
                        const proc = procedures.find(p => p.id === id);
                        if (!proc) return null;
                        const chipColor = proc.color || '#3788d8';
                        const chipBg = hexToAlpha(chipColor, 0.18);
                        const chipBorder = hexToAlpha(chipColor, 0.55);
                        return (
                          <span className="proc-chip" key={id} style={{ background: chipBg, borderColor: chipBorder, color: 'var(--ink)' }}>
                            {proc.name}
                            <button
                              type="button"
                              className="proc-chip-x"
                              onClick={() => toggleProcedure(id)}
                              aria-label={`${t('common.remove', 'Remover')} ${proc.name}`}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                      <input
                        type="text"
                        className="proc-search"
                        value={procedureSearch}
                        onChange={(e) => setProcedureSearch(e.target.value)}
                        placeholder={t('calendar.searchProcedure', 'Buscar procedimento…')}
                        aria-label={t('calendar.searchProcedure', 'Buscar procedimento')}
                      />
                    </div>
                    <div className="proc-options">
                      {(procedures || [])
                        .filter(p => normalizeText(p.name || '').includes(normalizeText(procedureSearch)))
                        .map(proc => {
                          const on = selectedProcedureIds.includes(proc.id);
                          const optColor = proc.color || '#3788d8';
                          const onBg = hexToAlpha(optColor, 0.18);
                          const checkBg = on ? optColor : undefined;
                          const checkBorder = on ? optColor : undefined;
                          return (
                            <button
                              type="button"
                              key={proc.id}
                              className={`proc-option ${on ? 'on' : ''}`}
                              onClick={() => toggleProcedure(proc.id)}
                              aria-pressed={on}
                              aria-label={proc.name}
                              style={on ? { background: onBg } : undefined}
                            >
                              <span className="proc-option-check" style={on ? { background: checkBg, borderColor: checkBorder } : undefined}>{on && <Check size={14} />}</span>
                              <span className="proc-option-swatch" style={{ background: optColor }} />
                              <span className="proc-option-name">{proc.name}</span>
                              <span className="proc-option-meta">{proc.durationMinutes || 30} min</span>
                            </button>
                          );
                        })}
                    </div>
                  </div>
                </div>

                {/* Date + Time */}
                <div className="field">
                  <label className="field-label">{t('calendar.date', 'Data e horário')}</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input
                      type="date"
                      value={formData.start instanceof Date && !isNaN(formData.start.getTime()) ? formatDateForInput(formData.start) : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          // Parse the "YYYY-MM-DD" value as LOCAL date components —
                          // `new Date(string)` parses date-only ISO strings as UTC
                          // midnight, which lands on the previous local day for
                          // timezones behind UTC (e.g. BRT) once setHours() below
                          // reapplies the local time-of-day.
                          const [year, month, day] = e.target.value.split('-').map(Number);
                          const newDate = new Date(formData.start);
                          newDate.setFullYear(year, month - 1, day);
                          const newEnd = new Date(newDate.getTime() + formData.durationMinutes * 60 * 1000);
                          setFormData({ ...formData, start: newDate, end: newEnd });
                        }
                      }}
                      style={{ flex: 1, minWidth: 0 }}
                      required
                    />
                    {!formData.allDay && (
                      <select
                        value={formatTimeForInput(formData.start)}
                        onChange={(e) => {
                          const newDate = parseTimeInput(e.target.value, formData.start);
                          const newEnd = new Date(newDate.getTime() + formData.durationMinutes * 60 * 1000);
                          setFormData({ ...formData, start: newDate, end: newEnd });
                        }}
                      >
                        {(() => {
                          const currentTime = formatTimeForInput(formData.start);
                          const slots = Array.from({ length: 24 }, (_, h) =>
                            [0, 15, 30, 45].map(m => `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`)
                          ).flat();
                          const allSlots = slots.includes(currentTime) ? slots : [...slots, currentTime].sort();
                          return allSlots.map(time => <option key={time} value={time}>{time}</option>);
                        })()}
                      </select>
                    )}
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 6, fontSize: 12.5, color: 'var(--ink-muted)' }}>
                    <input
                      type="checkbox"
                      checked={formData.allDay}
                      onChange={(e) => setFormData({ ...formData, allDay: e.target.checked })}
                      style={{ accentColor: 'var(--accent)' }}
                    />
                    {t('calendar.allDay', 'Dia inteiro')}
                  </label>
                </div>

                {/* Duration pills */}
                {!formData.allDay && (
                  <div className="field">
                    <label className="field-label">{t('calendar.duration', 'Duração')}</label>
                    <div className="dur-row">
                      {[15, 30, 45, 60, 90, 120].map(d => (
                        <button
                          key={d}
                          type="button"
                          className={`dur-pill ${formData.durationMinutes === d ? 'on' : ''}`}
                          onClick={() => {
                            const newEnd = new Date(formData.start.getTime() + d * 60 * 1000);
                            setFormData({ ...formData, durationMinutes: d, end: newEnd });
                          }}
                        >
                          {d} min
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Color dots */}
                <div className="field">
                  <label className="field-label">{t('calendar.color', 'Cor no calendário')}</label>
                  <div className="color-dot-row">
                    {colorOptions.map(c => (
                      <div
                        key={c}
                        className={`color-dot ${formData.color === c ? 'on' : ''}`}
                        style={{ background: c }}
                        onClick={() => setFormData({ ...formData, color: c })}
                      />
                    ))}
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      style={{ width: 22, height: 22, border: 'none', padding: 0, cursor: 'pointer', borderRadius: '50%' }}
                    />
                  </div>
                </div>

                {/* Location */}
                <div className="field">
                  <label className="field-label" htmlFor="eventLocation">{t('calendar.location', 'Local')}</label>
                  <input
                    id="eventLocation"
                    type="text"
                    aria-label="Location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder={t('calendar.eventLocation')}
                  />
                </div>

                {/* Notes */}
                <div className="field" style={{ marginBottom: 4 }}>
                  {/* Recurrence picker — only on new events */}
                  {!event?.id && (
                    <div className="field" style={{ marginBottom: 12 }}>
                      <label className="field-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Repeat size={13} /> {t('calendar.recurrence', 'Repetição')}
                      </label>
                      <select
                        value={formData.recurrenceRule}
                        onChange={e => setFormData({ ...formData, recurrenceRule: e.target.value })}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 14 }}
                      >
                        <option value="">{t('calendar.noRepeat', 'Não repete')}</option>
                        <option value="FREQ=DAILY;COUNT=5">{t('calendar.repeatDailyWeek', 'Diariamente (5x)')}</option>
                        <option value="FREQ=WEEKLY;COUNT=4">{t('calendar.repeatWeekly4', 'Semanalmente (4x)')}</option>
                        <option value="FREQ=WEEKLY;COUNT=8">{t('calendar.repeatWeekly8', 'Semanalmente (8x)')}</option>
                        <option value="FREQ=BIWEEKLY;INTERVAL=2;COUNT=6">{t('calendar.repeatBiweekly', 'A cada 2 semanas (6x)')}</option>
                        <option value="FREQ=MONTHLY;COUNT=3">{t('calendar.repeatMonthly3', 'Mensalmente (3x)')}</option>
                        <option value="FREQ=MONTHLY;COUNT=6">{t('calendar.repeatMonthly6', 'Mensalmente (6x)')}</option>
                        <option value="FREQ=MONTHLY;COUNT=12">{t('calendar.repeatMonthly12', 'Mensalmente (12x)')}</option>
                      </select>
                    </div>
                  )}

                  {/* Recurrence indicator on existing recurring events */}
                  {event?.recurrenceGroupId && (
                    <div style={{ fontSize: 12, color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 12, padding: '6px 10px', background: 'color-mix(in srgb,var(--accent) 8%,transparent)', borderRadius: 6 }}>
                      <Repeat size={12} /> {t('calendar.isRecurring', 'Evento recorrente')}
                    </div>
                  )}

                  {/* Recurrence edit mode dialog */}
                  {event?.recurrenceGroupId && recurrenceEditMode !== null && (
                    <div style={{ margin: '8px 0 12px', padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 8, background: 'color-mix(in srgb,var(--accent) 5%,transparent)' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{t('calendar.editWhich', 'Editar qual ocorrência?')}</div>
                      {(['this', 'following', 'all'] as const).map(mode => (
                        <label key={mode} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer', fontSize: 13 }}>
                          <input type="radio" name="recMode" value={mode} checked={recurrenceEditMode === mode} onChange={() => setRecurrenceEditMode(mode)} />
                          {mode === 'this'      && t('calendar.editThis',      'Este evento')}
                          {mode === 'following' && t('calendar.editFollowing', 'Este e os seguintes')}
                          {mode === 'all'       && t('calendar.editAll',       'Todos os eventos')}
                        </label>
                      ))}
                    </div>
                  )}

                  {/* Provider selector — admin only, multi-user clinic */}
                  {isAdmin && providers.length > 1 && (
                    <div className="field" style={{ marginBottom: 12 }}>
                      <label className="field-label">{t('calendar.provider', 'Profissional')}</label>
                      <select
                        value={formData.providerId ?? ''}
                        onChange={e => setFormData({ ...formData, providerId: e.target.value ? parseInt(e.target.value) : undefined })}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 14 }}
                      >
                        <option value="">{t('calendar.anyProvider', 'Qualquer profissional')}</option>
                        {providers.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Resource selector (salas) */}
                  {availableResources.length > 0 ? (
                    <div className="field" style={{ marginBottom: 12 }}>
                      <label className="field-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <DoorOpen size={13} /> {t('calendar.resources', 'Sala / Recurso')}
                      </label>
                      <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginBottom: 6 }}>
                        {t('calendar.resourcesHint', 'Marque aqui a sala em vez de escrever no título — assim dá para ver quem usa cada sala.')}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {availableResources.map(r => (
                          <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--ink)' }}>
                            <input
                              type="checkbox"
                              checked={selectedResourceIds.includes(r.id)}
                              onChange={() => toggleResource(r.id)}
                              style={{ accentColor: r.color }}
                            />
                            <span style={{ width: 10, height: 10, borderRadius: 3, background: r.color, flexShrink: 0 }} />
                            {r.name}
                            {r.description && <span style={{ color: 'var(--ink-faint)', fontSize: 11 }}>— {r.description}</span>}
                          </label>
                        ))}
                      </div>
                    </div>
                  ) : isAdmin ? (
                    <div className="field" style={{ marginBottom: 12 }}>
                      <label className="field-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <DoorOpen size={13} /> {t('calendar.resources', 'Sala / Recurso')}
                      </label>
                      <div style={{ fontSize: 12, color: 'var(--ink-faint)', lineHeight: 1.4 }}>
                        {t('calendar.resourcesEmpty', 'Nenhuma sala cadastrada. Cadastre suas salas em Painel Admin → Clínica para marcar quem usa cada sala em vez de escrever no título.')}
                      </div>
                    </div>
                  ) : null}

                  <label className="field-label" htmlFor="eventNotes">{t('calendar.notes', 'Anotações internas')}</label>
                  <textarea
                    id="eventNotes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder={t('calendar.appointmentNotes', 'Notas para a sessão…')}
                    rows={2}
                  />
                </div>

                {/* Photos Section */}
                {event && showPhotosSection && (
                  <div className="field" style={{ marginTop: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <label className="field-label">{t('calendar.photos', 'Fotos')}</label>
                      <button type="button" onClick={() => setShowPhotosSection(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-faint)' }}>
                        <X size={14} />
                      </button>
                    </div>
                    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius)', padding: 14, marginTop: 4 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 10 }}>{t('calendar.addNewPhoto')}</div>
                      <input
                        id="picture-upload-event"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileSelect}
                        style={{ fontSize: 13, marginBottom: 8 }}
                      />
                      <input
                        type="text"
                        value={pictureDescription}
                        onChange={(e) => setPictureDescription(e.target.value)}
                        placeholder={t('calendar.photoDescription')}
                        style={{ marginBottom: 8 }}
                      />
                      {selectedFiles.length > 0 && (
                        <div>
                          <div style={{ fontSize: 12, color: 'var(--ink-muted)', marginBottom: 6 }}>
                            {selectedFiles.map((file, i) => (
                              <span key={i}>{file.name} ({formatFileSize(file.size)}){i < selectedFiles.length - 1 ? ', ' : ''}</span>
                            ))}
                          </div>
                          <button type="button" className="btn-s primary" onClick={handleUploadPicture} disabled={uploadingPicture} style={{ fontSize: 12, padding: '6px 12px' }}>
                            <Upload size={12} />
                            {uploadingPicture ? t('common.uploading') : `${t('common.upload')} ${selectedFiles.length} ${selectedFiles.length > 1 ? t('common.photos') : t('common.photo')}`}
                          </button>
                        </div>
                      )}
                    </div>
                    {pictures.length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 10 }}>
                        {pictures.map((picture) => (
                          <div key={picture.id} style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', cursor: 'pointer' }}>
                            <div style={{ aspectRatio: '1', background: 'var(--bg-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <img
                                src={apiService.getImageUrl(picture.filename)}
                                alt={picture.description || picture.original_name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onClick={() => { setSelectedPicture(picture); setShowImageViewer(true); }}
                                onError={(e) => { (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOWNhM2FmIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+SW1hZ2VtPC90ZXh0Pjwvc3ZnPg=='; }}
                              />
                            </div>
                            <div style={{ padding: '6px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: 11, color: 'var(--ink-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{picture.original_name}</span>
                              <div style={{ display: 'flex', gap: 4 }}>
                                <button type="button" onClick={() => { setSelectedPicture(picture); setShowImageViewer(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', padding: 10, minWidth: 44, minHeight: 44, touchAction: 'manipulation' }}><Eye size={14} /></button>
                                <button type="button" onClick={() => handleDeletePicture(picture.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: 10, minWidth: 44, minHeight: 44, touchAction: 'manipulation' }}><Trash2 size={14} /></button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {event && !showPhotosSection && (
                  <button type="button" onClick={() => setShowPhotosSection(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                    <Eye size={14} /> {t('calendar.managePhotos', 'Fotos')} ({pictures.length})
                  </button>
                )}
              </form>
              <AppointmentSaveEffects patientId={formData.clientId} procedureName={(procedures.find((p: any) => p.id === (formData.procedureId || selectedProcedureIds[0])) as any)?.name} procedureId={formData.procedureId || selectedProcedureIds[0]} validityDays={(procedures.find((p: any) => p.id === (formData.procedureId || selectedProcedureIds[0])) as any)?.validity_days} sendConsent={sendConsent} sendIntake={sendIntake} />
            </div>

            {/* RIGHT — preview + reminders */}
            <div className="right">
              {/* Time strip preview */}
              {formData.start instanceof Date && !isNaN(formData.start.getTime()) && (
                <div className="timestrip">
                  <div className="day-meta">
                    <div className="d">{formData.start.getDate()}</div>
                    <div>
                      <div style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 500, color: 'var(--ink)' }}>
                        {formData.start.toLocaleDateString('pt-BR', { month: 'long' })}, {formData.start.toLocaleDateString('pt-BR', { weekday: 'short' })}
                      </div>
                      <div className="m" style={{ marginTop: 2 }}>{formData.start.getFullYear()}</div>
                    </div>
                  </div>

                  <div className="strip">
                    {(() => {
                      const startHour = Math.max(0, formData.start.getHours() - 1);
                      return Array.from({ length: 5 }, (_, i) => startHour + i).filter(h => h < 24).map(h => (
                        <div key={h} className="hour">
                          <span className="label">{`${h.toString().padStart(2, '0')}:00`}</span>
                        </div>
                      ));
                    })()}
                    {(() => {
                      const boxTop = 38 + ((formData.start.getMinutes() / 60) * 38);
                      const boxHeight = Math.max(28, (formData.durationMinutes / 60) * 38);
                      // Short events can't fit title + time inside the box, so the
                      // time spills out. Render it as a clean label below instead.
                      const isCompact = formData.durationMinutes < 60;
                      const timeLabel = `${formatTime(formData.start)} – ${formatTime(formData.end)} · ${formData.durationMinutes} min`;
                      return (
                        <>
                          <div className={`now${isCompact ? ' now--compact' : ''}`} style={{ top: boxTop, height: boxHeight }}>
                            <div className="t">{selectedProcedure?.name || formData.title || t('calendar.newEvent', 'Novo agendamento')}</div>
                            {!isCompact && <div className="s">{timeLabel}</div>}
                          </div>
                          {isCompact && (
                            <div className="now-time-out" style={{ top: boxTop + boxHeight + 3 }}>{timeLabel}</div>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  <div className="conflict-banner" style={{ background: 'color-mix(in srgb, var(--success) 10%, transparent)', borderColor: 'color-mix(in srgb, var(--success) 30%, transparent)' }}>
                    <span className="ic" style={{ color: 'var(--success)' }}><Check size={14} /></span>
                    <div>
                      <strong>{t('calendar.noConflicts', 'Sem conflitos.')}</strong>
                    </div>
                  </div>
                </div>
              )}

              {/* Reminders */}
              <div style={{ marginTop: 18 }}>
                <label className="field-label" style={{ display: 'block', marginBottom: 8 }}>{t('calendar.reminders', 'Lembretes automáticos')}</label>
                <div className="rmnd-card">
                  <button
                    type="button"
                    className="rmnd-row rmnd-row-btn"
                    onClick={() => setReminders(r => ({ ...r, wa: !r.wa }))}
                    aria-pressed={reminders.wa}
                    aria-label="Toggle WhatsApp reminder"
                  >
                    <div className="ch wa"><Phone size={14} /></div>
                    <div style={{ flex: 1 }}>
                      <div className="lbl">WhatsApp</div>
                      <div className="sub">24h antes</div>
                    </div>
                    <span className={`mswitch ${reminders.wa ? 'on' : ''}`} />
                  </button>
                  <button
                    type="button"
                    className="rmnd-row rmnd-row-btn"
                    onClick={() => setReminders(r => ({ ...r, email: !r.email }))}
                    aria-pressed={reminders.email}
                    aria-label="Toggle email reminder"
                  >
                    <div className="ch"><Mail size={14} /></div>
                    <div style={{ flex: 1 }}>
                      <div className="lbl">E-mail</div>
                      <div className="sub">3 dias antes</div>
                    </div>
                    <span className={`mswitch ${reminders.email ? 'on' : ''}`} />
                  </button>
                </div>
              </div>

              {/* ── Pre-consultation forms: termo + anamnese with the reminder ── */}
              {(hasConsentTemplates || hasIntakeTemplates) && (
                <div style={{ marginTop: 18 }}>
                  <label className="field-label" style={{ display: 'block', marginBottom: 8 }}>
                    {t('calendar.preConsultForms', 'Enviar antes da consulta')}
                  </label>
                  <div className="rmnd-card">
                    {hasConsentTemplates && (
                      <button
                        type="button"
                        className="rmnd-row rmnd-row-btn"
                        onClick={() => setSendConsent(v => !v)}
                        aria-pressed={sendConsent}
                        aria-label="Toggle consent term with reminder"
                      >
                        <div className="ch"><Check size={14} /></div>
                        <div style={{ flex: 1 }}>
                          <div className="lbl">{t('calendar.consentTerm', 'Termo de consentimento')}</div>
                          <div className="sub">{t('calendar.linkWithReminder', 'Link junto ao lembrete')}</div>
                        </div>
                        <span className={`mswitch ${sendConsent ? 'on' : ''}`} />
                      </button>
                    )}
                    {hasIntakeTemplates && (
                      <button
                        type="button"
                        className="rmnd-row rmnd-row-btn"
                        onClick={() => setSendIntake(v => !v)}
                        aria-pressed={sendIntake}
                        aria-label="Toggle intake form with reminder"
                      >
                        <div className="ch"><User size={14} /></div>
                        <div style={{ flex: 1 }}>
                          <div className="lbl">{t('calendar.intakeForm', 'Ficha de anamnese')}</div>
                          <div className="sub">{t('calendar.linkWithReminder', 'Link junto ao lembrete')}</div>
                        </div>
                        <span className={`mswitch ${sendIntake ? 'on' : ''}`} />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* ── Teleconsulta section (Onda 3B) ───────────────────── */}
              <div style={{ marginTop: 18, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                <button
                  type="button"
                  className="btn-s ghost"
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 13 }}
                  onClick={() => setTeleconsultaOpen(o => !o)}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <DoorOpen size={14} />
                    {t('calendar.teleconsulta', 'Teleconsulta / Videochamada')}
                    {meetUrl && videoProvider !== 'none' && (
                      <span style={{ background: 'var(--accent)', color: '#fff', borderRadius: 20, fontSize: 10, padding: '1px 6px', marginLeft: 4 }}>
                        {t('calendar.teleconsultaActive', 'ativo')}
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--ink-faint)' }}>{teleconsultaOpen ? '▲' : '▼'}</span>
                </button>

                {teleconsultaOpen && (
                  <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                      <label className="lbl" style={{ display: 'block', marginBottom: 4 }}>
                        {t('calendar.videoProvider', 'Plataforma')}
                      </label>
                      <select
                        className="input-s"
                        style={{ width: '100%' }}
                        value={videoProvider}
                        onChange={e => {
                          const val = e.target.value;
                          setVideoProvider(val);
                          if (val === 'jitsi') {
                            const tenantSlug = (window as any).__TENANT_SLUG__ || 'clinic';
                            const appointmentId = event?.id || Math.random().toString(36).slice(2, 8);
                            setMeetUrl(`https://meet.jit.si/${tenantSlug}-${appointmentId}`);
                          } else if (val === 'none') {
                            setMeetUrl('');
                          }
                        }}
                      >
                        <option value="none">{t('calendar.videoNone', 'Nenhuma')}</option>
                        <option value="jitsi">{t('calendar.videoJitsi', 'Jitsi (automático)')}</option>
                        <option value="google_meet">{t('calendar.videoGoogleMeet', 'Google Meet')}</option>
                        <option value="zoom">{t('calendar.videoZoom', 'Zoom')}</option>
                        <option value="custom">{t('calendar.videoCustom', 'Link personalizado')}</option>
                      </select>
                    </div>

                    {videoProvider !== 'none' && (
                      <div>
                        <label className="lbl" style={{ display: 'block', marginBottom: 4 }}>
                          {t('calendar.meetUrl', 'Link da reunião')}
                        </label>
                        <input
                          type="url"
                          className="input-s"
                          style={{ width: '100%' }}
                          value={meetUrl}
                          onChange={e => setMeetUrl(e.target.value)}
                          placeholder="https://..."
                        />
                        {meetUrl && (
                          <a
                            href={meetUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-s primary"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, textDecoration: 'none', fontSize: 12 }}
                          >
                            <ExternalLink size={12} />
                            {t('calendar.joinConsultation', 'Entrar na consulta')}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Send Message button */}
              {event && (
                <button
                  type="button"
                  onClick={() => setShowSendMessage(true)}
                  className="btn-s ghost"
                  style={{ width: '100%', marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                >
                  <Send size={14} /> {t('calendar.sendMessage', 'Enviar mensagem')}
                </button>
              )}
            </div>
          </ModalBody>

          <ModalFooter>
            <div className="spacer" />
            <div className="kbd-hint">
              <kbd>⌘</kbd> <kbd>↵</kbd> {t('calendar.toConfirm', 'para confirmar')}
            </div>
            {onDelete && (
              <button className="btn-s danger" type="button" onClick={handleDelete}>
                <Trash2 size={14} /> {t('common.delete', 'Excluir')}
              </button>
            )}
            <button className="btn-s ghost" type="button" onClick={onClose}>{t('common.cancel')}</button>
            <button className="btn-s primary" type="button" onClick={handleSave} disabled={saving}>
              <Check size={14} />
              {saving ? t('common.saving', 'Salvando…') : event?.id ? t('common.update', 'Salvar') : t('common.create', 'Confirmar')}
            </button>
          </ModalFooter>
        </>
      )}

      {/* Image Viewer Modal */}
      {showImageViewer && selectedPicture && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4" style={{ zIndex: 2000 }}>
          <div className="relative max-w-6xl max-h-full">
            <button
              onClick={() => { setShowImageViewer(false); setSelectedPicture(null); setImageZoom(1); }}
              className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-75 transition-all"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="absolute top-4 left-4 z-10 flex gap-2">
              <button onClick={() => setImageZoom(prev => Math.max(0.5, prev - 0.25))} className="bg-black bg-opacity-50 text-white rounded px-3 py-1 hover:bg-opacity-75 transition-all" disabled={imageZoom <= 0.5}>-</button>
              <button onClick={() => setImageZoom(1)} className="bg-black bg-opacity-50 text-white rounded px-3 py-1 hover:bg-opacity-75 transition-all">1:1</button>
              <button onClick={() => setImageZoom(prev => Math.min(3, prev + 0.25))} className="bg-black bg-opacity-50 text-white rounded px-3 py-1 hover:bg-opacity-75 transition-all" disabled={imageZoom >= 3}>+</button>
            </div>
            <img
              src={apiService.getImageUrl(selectedPicture.filename)}
              alt={selectedPicture.description || selectedPicture.original_name}
              className="max-w-full max-h-full object-contain rounded-lg"
              style={{ transform: `scale(${imageZoom})`, transformOrigin: 'center', transition: 'transform 0.3s ease' }}
              onError={(e) => { (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOWNhM2FmIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+SW1hZ2VtPC90ZXh0Pjwvc3ZnPg=='; }}
            />
            <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-75 text-white p-4 rounded-lg">
              <h3 className="text-lg font-semibold mb-2">{selectedPicture.original_name}</h3>
              {selectedPicture.description && <p className="text-sm mb-2">{selectedPicture.description}</p>}
              <div className="text-xs text-gray-300">{formatFileSize(selectedPicture.file_size)} · {formatUploadDate(selectedPicture.uploaded_at)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Send Message Panel */}
      {showSendMessage && event && (() => {
        const patient = event.clientId ? clients.find(c => c.id === event.clientId) : null;
        return (
          <SendMessagePanel
            appointmentId={event.id}
            patientName={event.clientName}
            patientPhone={patient?.phone}
            appointmentDate={event.start}
            appointmentTime={event.start.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            onClose={() => setShowSendMessage(false)}
            onSuccess={() => setShowSendMessage(false)}
          />
        );
      })()}
    </ModalShell>
  );
};

export default EventModal;
